'use client'

import AmenitiesFilter from '@/app/components/ResultsFilters/AmenitiesFilter'
import FilterButtons from '@/app/components/ResultsFilters/FilterButtons'
import GuestRatingFilter from '@/app/components/ResultsFilters/GuestRatingFilter'
import HotelStyleFilter from '@/app/components/ResultsFilters/HotelStyleFilter'
import PopularFilters from '@/app/components/ResultsFilters/PopularFilters'
import PriceFilter from '@/app/components/ResultsFilters/PriceFilter'
import StarRatingFilter from '@/app/components/ResultsFilters/StarRatingFilter'
import StayOptionsFilter from '@/app/components/ResultsFilters/StayOptionsFilter'
import {
	DEFAULT_CURRENCY,
	FILTER_TYPES,
	INIT_DATA_STORAGE_KEY,
	XL_BREAKPOINT,
} from '@/app/constants'
import {
	emitRpFilterAmenitiesSelectGAEvent,
	emitRpFilterGuestRatingSelectGAEvent,
	emitRpFilterHotelStyleSelectGAEvent,
	emitRpFilterPopularSelectGAEvent,
	emitRpFilterPriceSelectGAEvent,
	emitRpFilterStarsSelectGAEvent,
	emitRpFilterStaySelectGAEvent,
} from '@/app/ga-event-helpers'
import { DestPagesProps, Filters, FiltersCountMap, UserData } from '@/app/types'
import { isApartment, isRtlLanguage, smoothScrollTo } from '@/app/utils'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import { useTranslations } from 'next-intl'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

export interface ResultsFiltersProps {
	pageProps: DestPagesProps
	defaultValueStars?: number
	defaultValueRating?: number
	availableRates: { min: number; max: number }
	availableFilters: FiltersCountMap
	onClose?: () => void
}

const ResultsFilters: FC<ResultsFiltersProps> = ({
	pageProps,
	defaultValueStars = 0,
	defaultValueRating = 0,
	availableRates,
	availableFilters,
	onClose,
}) => {
	const t = useTranslations('Keywords')
	const router = useRouter()
	const [isOpen, setIsOpen] = useState(false)
	const [isDirty, setIsDirty] = useState(false)
	const query = useSearchParams()
	const [value, setValue] = useState<Filters>({
		stars: [],
		rating: [],
		facilities: [],
		stayOptions: [],
		priceRange: [availableRates.min, availableRates.max],
	})
	const filterRef = useRef<HTMLDivElement>(null)
	const isDesktop = useMediaQuery(XL_BREAKPOINT)
	const params = useParams<DestPagesProps['params']>()
	const selectedLanguage = params.lang
	const isRtl = isRtlLanguage(selectedLanguage)
	const [showAllFacilities, setShowAllFacilities] = useState(false)
	const [showAllStayOptions, setShowAllStayOptions] = useState(false)
	const [showAllHotelStyles, setShowAllHotelStyles] = useState(false)
	const filterBottomRef = useRef<HTMLDivElement>(null)
	const [isFilterSticky, setIsFilterSticky] = useState(false)
	const [isFilterUpdate, setIsFilterUpdate] = useState(false)

	const [initData, setItem] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const userCurrency = initData?.selectedCurrency
	const currency =
		query.get('currency') || userCurrency?.abbreviation || DEFAULT_CURRENCY

	const toggleFacilitiesView = () => {
		setShowAllFacilities(!showAllFacilities)
	}

	const toggleStayOptionsView = () => {
		setShowAllStayOptions(!showAllStayOptions)
	}
	const toggleHotelStylesView = () => {
		setShowAllHotelStyles(!showAllHotelStyles)
	}

	useEffect(() => {
		const initialValue = getValue()
		setValue(initialValue)
	}, [pageProps.searchParams])

	const parseParamToNumbers = (param: string | undefined): number[] => {
		if (!param || param === 'undefined') return []
		return param
			.split('|')
			.map(Number)
			.filter((n) => !isNaN(n))
	}

	const parseRatingParam = (param: string | undefined): number[] => {
		if (!param || param === 'undefined') return []
		const numbers = parseParamToNumbers(param)
		return numbers.length > 0 ? [Math.min(...numbers)] : []
	}

	const getValue = (): Filters => {
		const {
			s: starsParam,
			gr: ratingParam,
			mn: minPrice,
			mx: maxPrice,
			fa: facilitiesParam,
			p: stayOptionsParam,
			t: themesParam,
		} = pageProps.searchParams

		const stars = parseParamToNumbers(starsParam)
		const rating = parseRatingParam(ratingParam)
		const facilities = parseParamToNumbers(facilitiesParam)
		const stayOptions = parseParamToNumbers(stayOptionsParam)
		const hotelStyles = parseParamToNumbers(themesParam)

		let priceRange: [number, number]

		if (minPrice || maxPrice) {
			const min = minPrice ? +minPrice : availableRates.min
			const max = maxPrice ? +maxPrice : availableRates.max
			priceRange = [min, max]
		} else {
			priceRange = [availableRates.min, availableRates.max]
		}

		return {
			stars,
			rating,
			facilities,
			stayOptions,
			hotelStyles,
			priceRange,
		}
	}

	const [localValue, setLocalValue] = useState<Filters>(getValue())

	const applyFilters = () => {
		setIsFilterUpdate(true)
		setValue(localValue)
		const { s, gr, page, mn, mx, fa, p, ...restParams } = pageProps.searchParams
		const newQuery = generateNewQuery(localValue, restParams)
		updateURL(newQuery)
		setIsDirty(false)
		onClose && onClose()
	}

	const handleReset = () => {
		// emitFilterResetClickGAEvent()
		const resetValue: Filters = {
			stars: [],
			rating: [],
			priceRange: [availableRates.min, availableRates.max],
			facilities: [],
			stayOptions: [],
			hotelStyles: [],
		}
		setValue(resetValue)
		setLocalValue(resetValue)
		setIsDirty(false)
		const { s, gr, page, mn, mx, fa, p, t, ...restParams } =
			pageProps.searchParams

		const searchParams = new URLSearchParams(
			Object.fromEntries(
				Object.entries(restParams).map(([key, value]) => [
					key,
					Array.isArray(value) ? value.join(',') : value,
				])
			)
		)
		const newUrl = `${window.location.pathname}?${searchParams.toString()}`
		router.push(newUrl)
	}

	const updateLocalValue = (newPartialValue: Partial<Filters>) => {
		setLocalValue((prev: Filters) => ({ ...prev, ...newPartialValue }))
		setIsDirty(true)

		if (isDesktop) {
			setIsFilterUpdate(true)
		}
	}

	const handleFacilityChange = (facilityId: number, name?: string) => {
		const isNewValue = !localValue.facilities.includes(facilityId)

		const newFacilities = !isNewValue
			? localValue.facilities.filter((f: number) => f !== facilityId)
			: [...localValue.facilities, facilityId]

		updateLocalValue({ facilities: newFacilities })

		if (name) {
			// Name is provided, emit GA event for amenity selection
			emitRpFilterAmenitiesSelectGAEvent(facilityId, isNewValue, name)
		}

		if (isDesktop) {
			smoothScrollTo(0, 0)
			setValue((prevValue: Filters) => ({
				...prevValue,
				facilities: newFacilities,
			}))
		}

		return isNewValue
	}

	const handleStayOptionChange = (optionId: number, name?: string) => {
		const isNewValue = !localValue.stayOptions.includes(optionId)
		let newOptions = isNewValue
			? [...localValue.stayOptions, optionId]
			: localValue.stayOptions.filter((f) => f !== optionId)

		let newStars = [...localValue.stars]

		const hasApartmentOption = (options: number[]) => options.some(isApartment)

		const hasHotelOption = (options: number[]) =>
			options.some((option) => !isApartment(option))

		const hasNonZeroStarFilter = newStars.some((star) => star !== 0)
		const hasZeroStar = localValue.stars.includes(0)

		if (isApartment(optionId)) {
			if (isNewValue) {
				// Adding an apartment option
				if (hasHotelOption(newOptions)) {
					// Both apartments and hotels are selected
					if (hasNonZeroStarFilter && !newStars.includes(0)) {
						// Star filters exist and don't include 0, add 0
						newStars.push(0)
					}
				} else {
					// Only apartments, ensure 0 is included
					if (!hasZeroStar) {
						newStars = [...localValue.stars, 0]
					} else {
						newStars = [...localValue.stars]
					}
				}
			} else {
				// Removing an apartment option
				if (!hasApartmentOption(newOptions)) {
					newStars = newStars.filter((star) => star !== 0)
				}
			}
		} else {
			// Hotel option
			if (isNewValue) {
				// Adding a hotel option
				if (hasApartmentOption(newOptions)) {
					// Both apartments and hotels are selected
					if (newStars.length === 1 && newStars[0] === 0) {
						// Only 0 star filter, remove it
						newStars = []
					}
				} else {
					// Only hotels, remove 0 if present
					newStars = newStars.filter((star) => star !== 0)
				}
			} else {
				// Removing a hotel option
				if (!hasHotelOption(newOptions)) {
					if (hasApartmentOption(newOptions)) {
						// Only apartments left, ensure 0 is present among other stars
						if (!newStars.includes(0)) {
							newStars = [...localValue.stars, 0]
						} else {
							newStars = [...localValue.stars]
						}
					} else {
						// No stay options left
						newStars = [...localValue.stars]
					}
				}
			}
		}

		if (name) {
			emitRpFilterStaySelectGAEvent(name, isNewValue)
		}

		updateLocalValue({ stayOptions: newOptions, stars: newStars })

		if (isDesktop) {
			smoothScrollTo(0, 0)
			setValue((prevValue: Filters) => ({
				...prevValue,
				stayOptions: newOptions,
				stars: newStars,
			}))
		}

		return isNewValue
	}

	const handleStarChange = (star: number, track?: boolean) => {
		const isNewValue = !localValue.stars.includes(star)
		const hasApartment = localValue.stayOptions.some(isApartment)
		const hasZeroStar = localValue.stars.includes(0)

		let newStars: number[]

		if (isNewValue) {
			// Adding a new star
			if (hasApartment) {
				if (hasZeroStar) {
					newStars = [...localValue.stars, star]
				} else {
					newStars = [0, ...localValue.stars, star]
				}
			} else {
				newStars = localValue.stars.filter((s) => s !== 0).concat(star)
			}
		} else {
			// Removing a star
			newStars = localValue.stars.filter((s) => s !== star)
			if (newStars.length === 0) {
				newStars = []
			}
			if (newStars.length === 1 && newStars[0] === 0) {
				newStars = []
			}
		}

		updateLocalValue({ stars: newStars })

		if (track) {
			emitRpFilterStarsSelectGAEvent(star, isNewValue)
		}

		if (isDesktop) {
			smoothScrollTo(0, 0)
			setValue((prevValue: Filters) => ({ ...prevValue, stars: newStars }))
		}

		return isNewValue
	}

	const handleRatingChange = (ratingValue: number, name?: string) => {
		const newRating = localValue.rating.includes(ratingValue)
			? [] // Remove the filter completely if it's already selected
			: [ratingValue] // Add the new rating

		updateLocalValue({ rating: newRating })

		if (name) {
			emitRpFilterGuestRatingSelectGAEvent(name, newRating.length > 0)
		}

		if (isDesktop) {
			smoothScrollTo(0, 0)
			setValue((prevValue: Filters) => ({ ...prevValue, rating: newRating }))
		}

		return newRating.length > 0
	}

	const handleHotelStyleChange = (styleId: number, name?: string) => {
		const isNewValue = !localValue.hotelStyles?.includes(styleId)

		const newStyles = localValue.hotelStyles?.includes(styleId)
			? localValue.hotelStyles.filter((s: number) => s !== styleId)
			: [...(localValue.hotelStyles || []), styleId]

		updateLocalValue({ hotelStyles: newStyles })

		if (name) {
			emitRpFilterHotelStyleSelectGAEvent(name, isNewValue)
		}

		if (isDesktop) {
			smoothScrollTo(0, 0)

			setValue((prevValue: Filters) => ({
				...prevValue,
				hotelStyles: newStyles,
			}))
		}

		return isNewValue
	}

	const handlePriceRangeChange = (newValues: [number, number]) => {
		updateLocalValue({ priceRange: newValues })
		emitRpFilterPriceSelectGAEvent(newValues, currency)

		if (isDesktop) {
			smoothScrollTo(0, 0)

			setValue((prevValue: Filters) => ({
				...prevValue,
				priceRange: newValues,
			}))
		}
	}

	const handlePopularFilterChange = (filter: {
		id: number
		type: string
		name: string
	}) => {
		let isNewValue = false
		switch (filter.type) {
			case FILTER_TYPES.STARS:
				isNewValue = handleStarChange(Number(filter.id))
				break
			case FILTER_TYPES.STAY_OPTIONS:
				isNewValue = handleStayOptionChange(Number(filter.id))
				break
			case FILTER_TYPES.RATING:
				isNewValue = handleRatingChange(Number(filter.id))
				break
			case FILTER_TYPES.FACILITIES:
				isNewValue = handleFacilityChange(Number(filter.id))
				break
			default:
				console.log('Unknown filter type')
		}

		emitRpFilterPopularSelectGAEvent(filter.name, isNewValue)
	}

	const generateNewQuery = (currentValue: Filters, restParams: any) => {
		return {
			...restParams,
			...(currentValue.stars.length > 0
				? { s: currentValue.stars.join('|') }
				: {}),
			...(currentValue.rating.length > 0 && currentValue.rating[0] !== 0
				? {
						gr: Array.from(
							{ length: 11 - Math.min(...currentValue.rating) },
							(_, i) => i + Math.min(...currentValue.rating)
						).join('|'),
				  }
				: {}),
			...(currentValue.facilities.length > 0
				? { fa: currentValue.facilities.join('|') }
				: {}),
			...(currentValue.stayOptions.length > 0
				? { p: currentValue.stayOptions.join('|') }
				: {}),
			...(currentValue.hotelStyles?.length
				? { t: currentValue.hotelStyles.join('|') }
				: {}),
			...(currentValue.priceRange
				? {
						...(Math.abs(currentValue.priceRange[0] - availableRates.min) > 1 &&
							currentValue.priceRange[0] > 0 && {
								mn: Math.round(currentValue.priceRange[0]).toString(),
							}),
						...(Math.abs(currentValue.priceRange[1] - availableRates.max) > 1 &&
							currentValue.priceRange[1] > 0 && {
								mx: Math.round(currentValue.priceRange[1]).toString(),
							}),
				  }
				: {}),
		}
	}

	const updateURL = useCallback(
		(newQuery: any) => {
			if (!isFilterUpdate) return

			const searchParams = new URLSearchParams()

			Object.entries(newQuery).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					value.forEach((item) => searchParams.append(key, item))
				} else {
					searchParams.append(key, value as string)
				}
			})

			const newUrl = `${window.location.pathname}?${searchParams.toString()}`
			router.push(newUrl, { scroll: false })

			setIsFilterUpdate(false)
		},
		[isFilterUpdate]
	)

	useEffect(() => {
		if (isFilterUpdate) {
			const { s, gr, page, mn, mx, fa, t, p, ap, ...restParams } =
				pageProps.searchParams
			const newQuery = generateNewQuery(value, restParams)
			updateURL(newQuery)
		}
	}, [value, pageProps.searchParams, isFilterUpdate])

	useEffect(() => {
		const initialValue = getValue()

		setValue(initialValue)
		setLocalValue(initialValue)
		setIsDirty(false)
	}, [pageProps.searchParams, availableRates])

	const countActiveFilters = (filters: Filters): number => {
		const { stars, rating, facilities, stayOptions, hotelStyles, priceRange } =
			filters
		const starsWithoutZero = stars.filter((r) => r !== 0)
		const ratingWithoutZero = rating.filter((r) => r !== 0)

		let count =
			starsWithoutZero.length +
			ratingWithoutZero.length +
			facilities.length +
			stayOptions.length +
			(hotelStyles?.length || 0)

		// Count price range filter
		if (priceRange && availableRates) {
			if (
				Math.abs(priceRange[0] - availableRates.min) > 1 ||
				Math.abs(priceRange[1] - availableRates.max) > 1
			) {
				count += 1
			}
		}

		return count
	}

	const options = {
		threshold: 0,
		rootMargin: '-116px 0px 0px 0px',
	}

	const observerCallback = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const filterEntry = entries.find(
				(entry) => entry.target === filterRef.current
			)
			const bottomEntry = entries.find(
				(entry) => entry.target === filterBottomRef.current
			)

			if (filterEntry && bottomEntry) {
				if (
					!filterEntry.isIntersecting &&
					filterEntry.boundingClientRect.top < 0
				) {
					setIsFilterSticky(true)
				} else if (bottomEntry.isIntersecting) {
					setIsFilterSticky(false)
				}
			}
		},
		[]
	)

	useEffect(() => {
		if (isDesktop) {
			const observer = new IntersectionObserver(observerCallback, options)

			const currentFilterRef = filterRef.current
			const currentFilterBottomRef = filterBottomRef.current

			if (currentFilterRef) {
				observer.observe(currentFilterRef)
			}

			if (currentFilterBottomRef) {
				observer.observe(currentFilterBottomRef)
			}

			return () => {
				if (currentFilterRef) {
					observer.unobserve(currentFilterRef)
				}
				if (currentFilterBottomRef) {
					observer.unobserve(currentFilterBottomRef)
				}
			}
		}
	}, [isDesktop, observerCallback, filterRef.current, filterBottomRef.current])

	if (!availableFilters) return null

	return (
		<div className="hotel-list-filters select-none z-18" ref={filterRef}>
			<div className="hotel-list-filters__container">
				<div className="max-xl:px-8 max-xl:py-4 max-xl:pb-12 max-xl:max-w-screen">
					<PopularFilters
						localValue={localValue}
						handlePopularFilterChange={handlePopularFilterChange}
						isFilterSticky={isFilterSticky}
						availableFilters={availableFilters}
					/>
					<PriceFilter
						localValue={localValue}
						availableRates={availableRates}
						handlePriceRangeChange={handlePriceRangeChange}
					/>
					<StayOptionsFilter
						localValue={localValue}
						handleStayOptionChange={handleStayOptionChange}
						showAllStayOptions={showAllStayOptions}
						toggleStayOptionsView={toggleStayOptionsView}
						availableFilters={availableFilters?.propertyTypes}
					/>
					<GuestRatingFilter
						localValue={localValue}
						handleRatingChange={handleRatingChange}
						availableFilters={availableFilters?.guestRatings}
					/>
					<StarRatingFilter
						localValue={localValue}
						handleStarChange={handleStarChange}
						availableFilters={availableFilters?.starRatings}
					/>
					<AmenitiesFilter
						localValue={localValue}
						handleFacilityChange={handleFacilityChange}
						showAllFacilities={showAllFacilities}
						toggleFacilitiesView={toggleFacilitiesView}
						availableFilters={availableFilters?.facilities}
					/>
					<HotelStyleFilter
						localValue={localValue}
						handleHotelStyleChange={handleHotelStyleChange}
						showAllHotelStyles={showAllHotelStyles}
						toggleHotelStylesView={toggleHotelStylesView}
						availableFilters={availableFilters?.hotelThemes}
					/>
					<div ref={filterBottomRef} style={{ height: '1px', width: '100%' }} />
				</div>

				{!isDesktop && (
					<FilterButtons
						localValue={localValue}
						handleReset={handleReset}
						applyFilters={applyFilters}
						countActiveFilters={countActiveFilters}
					/>
				)}
			</div>
		</div>
	)
}

export default ResultsFilters
